import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, InfoLine, SearchLine} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import type {JiraObject} from 'client';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {t} from 'i18next';
import {type RefCallback, useEffect, useMemo, useRef, useState} from 'react';

import styles from './WorkstreamManageOutcomesModal.module.css';
import {getPlainTextTitle} from './workstreamManageOutcomesUtils';
import type {WorkstreamMetricPickerOption} from './workstreamMetricPickerOptions';

export type JiraPickerOption = {
  kind: 'jira';
  optionId: string;
  title: string;
  subtitle?: string;
  jiraObject: JiraObject;
};

export type ManageOutcomePickerOption = WorkstreamMetricPickerOption | JiraPickerOption;

type TypeaheadOption =
  | ManageOutcomePickerOption
  | {
      kind: 'create';
      optionId: 'create';
    }
  | {
      kind: 'section';
      optionId: 'linked-workstream-section' | 'linked-jira-section';
      title: string;
      tooltip: string;
    };

const getOptionsSignature = (options: ManageOutcomePickerOption[]) =>
  JSON.stringify(
    options.map((option) => [option.optionId, option.title, option.subtitle ?? '', option.kind])
  );

const useStableOptions = (options: ManageOutcomePickerOption[]) => {
  const stableOptionsRef = useRef<{
    options: ManageOutcomePickerOption[];
    signature: string;
  } | null>(null);
  const signature = getOptionsSignature(options);

  if (stableOptionsRef.current?.signature !== signature) {
    stableOptionsRef.current = {options, signature};
  }

  return stableOptionsRef.current.options;
};

export const WorkstreamOutcomePicker = ({
  value,
  isInvalid = false,
  placeholder,
  isLoading,
  isLoadingLinkedOptions,
  isLoadingJiraOptions,
  options,
  fieldRef,
  onChange,
  onFocus,
  onBlur,
  onSelect,
  onCreate,
  createType,
}: {
  value: string;
  isInvalid?: boolean;
  placeholder: string;
  isLoading: boolean;
  isLoadingLinkedOptions: boolean;
  isLoadingJiraOptions: boolean;
  options: ManageOutcomePickerOption[];
  fieldRef?: RefCallback<HTMLElement>;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSelect: (option: ManageOutcomePickerOption) => void;
  onCreate: () => void;
  createType: 'metric' | 'outcome';
}) => {
  const stableOptions = useStableOptions(options);
  const workstreamOptions = useMemo(
    () => stableOptions.filter((option) => option.kind === 'workstream'),
    [stableOptions]
  );
  const jiraOptions = useMemo(
    () => stableOptions.filter((option) => option.kind === 'jira'),
    [stableOptions]
  );
  const shouldShowLinkedSection = workstreamOptions.length > 0 || isLoadingLinkedOptions;
  const searchableOptions = useMemo<TypeaheadOption[]>(() => {
    const createOption =
      value.trim().length > 0 ? [{kind: 'create' as const, optionId: 'create' as const}] : [];
    return [
      ...createOption,
      ...(shouldShowLinkedSection
        ? [
            {
              kind: 'section' as const,
              optionId: 'linked-workstream-section' as const,
              title: t(
                createType === 'metric'
                  ? 'manage_outcomes_linked_metrics_title'
                  : 'manage_outcomes_linked_milestones_title'
              ).toString(),
              tooltip: t(
                createType === 'metric'
                  ? 'manage_outcomes_linked_metrics_tooltip'
                  : 'manage_outcomes_linked_milestones_tooltip'
              ).toString(),
            },
            ...workstreamOptions,
          ]
        : []),
      ...(createType === 'outcome' && (jiraOptions.length || isLoadingJiraOptions)
        ? [
            {
              kind: 'section' as const,
              optionId: 'linked-jira-section' as const,
              title: t('manage_outcomes_linked_jira_title').toString(),
              tooltip: t('manage_outcomes_linked_jira_tooltip').toString(),
            },
            ...jiraOptions,
          ]
        : []),
    ];
  }, [
    createType,
    isLoadingJiraOptions,
    jiraOptions,
    shouldShowLinkedSection,
    value,
    workstreamOptions,
  ]);
  const initialResults = useMemo(() => {
    let visibleResultCount = 0;
    let resultCount = 0;
    for (const option of searchableOptions) {
      if (option.kind === 'create') {
        resultCount += 1;
        continue;
      }
      if (option.kind === 'section') {
        if (visibleResultCount >= 6) {
          break;
        }
        resultCount += 1;
        continue;
      }
      if (visibleResultCount >= 6) {
        break;
      }
      visibleResultCount += 1;
      resultCount += 1;
    }
    return resultCount;
  }, [searchableOptions]);
  const renderSectionTitle = ({title, tooltip}: {title: string; tooltip: string}) => (
    <span className={styles.metricTitleTypeaheadSectionTitle}>
      <Eyebrow>{title}</Eyebrow>
      <Tooltip title={tooltip} placement="top">
        <span
          className={styles.metricTitleTypeaheadSectionInfoIcon}
          aria-label={tooltip}
          role="img"
          tabIndex={0}
        >
          <UIIcon src={InfoLine} size="small" />
        </span>
      </Tooltip>
    </span>
  );
  const openMenuRef = useRef<((options: {autoFocus?: boolean}) => void) | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isCreateRowHighlighted, setIsCreateRowHighlighted] = useState(Boolean(value.trim()));
  useEffect(() => {
    if (isInputFocused && (isLoading || searchableOptions.length > 0)) {
      openMenuRef.current?.({autoFocus: false});
    }
  }, [isInputFocused, isLoading, searchableOptions.length]);
  useEffect(() => {
    setIsCreateRowHighlighted(Boolean(value.trim()));
  }, [value]);
  const isCreatingRef = useRef(false);
  const hasNavigatedResultsRef = useRef(false);
  const clearCreateRowHighlight = () => {
    setIsCreateRowHighlighted(false);
  };
  const createFromSearch = () => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    onCreate();
    queueMicrotask(() => {
      isCreatingRef.current = false;
    });
  };

  return (
    <Typeahead.Wrapper
      isPortaled
      hasMaxHeight={320}
      openMenuOnFocus
      shouldHighlightFirstRow={false}
      onSelection={(optionId) => {
        if (String(optionId) === 'create') {
          createFromSearch();
          return;
        }
        const selectedOption = stableOptions.find((option) => option.optionId === String(optionId));
        if (selectedOption) {
          onSelect(selectedOption);
        }
      }}
    >
      {({getContentProps, getTriggerProps, openMenu}) => {
        openMenuRef.current = openMenu;
        return (
          <div ref={fieldRef} className={styles.metricTitleTypeahead}>
            <TextInput
              size="small"
              isInvalid={isInvalid}
              value={value}
              className={cx(styles.titleInput, styles.metricTitleTypeaheadInput)}
              withLeftAccessory={
                <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} size="small" />
              }
              placeholder={placeholder}
              autoComplete="off"
              {...getTriggerProps({
                onChange: (event) => {
                  hasNavigatedResultsRef.current = false;
                  setIsCreateRowHighlighted(true);
                  onChange((event.currentTarget as HTMLInputElement).value);
                },
                onFocus: () => {
                  setIsInputFocused(true);
                  hasNavigatedResultsRef.current = false;
                  setIsCreateRowHighlighted(Boolean(value.trim()));
                  onFocus?.();
                },
                onClick: () => openMenu({autoFocus: false}),
                onKeyDown: (event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    hasNavigatedResultsRef.current = true;
                    setIsCreateRowHighlighted(false);
                  }
                  if (event.key === 'Enter' && value.trim() && !hasNavigatedResultsRef.current) {
                    event.preventDefault();
                    createFromSearch();
                  }
                },
                onBlur: () => {
                  setIsInputFocused(false);
                  onBlur?.();
                },
              })}
            />
            <Typeahead.Container
              {...getContentProps({
                className: styles.metricTitleTypeaheadContent,
                onWheelCapture: (event: React.WheelEvent) => {
                  event.stopPropagation();
                },
                onTouchMove: (event: React.TouchEvent) => {
                  event.stopPropagation();
                },
              })}
              auto
              placement="bottom-start"
              positioningStrategy="fixed"
              triggerOffset={5}
              elementResize
            >
              {searchableOptions.length ? (
                <Typeahead.Results
                  initialResults={initialResults}
                  maxResults={searchableOptions.length}
                  results={searchableOptions}
                  renderRow={(option) => {
                    if (option.kind === 'create') {
                      const createLabel = t('manage_outcomes_create_from_search', {
                        name: value.trim(),
                        type:
                          createType === 'metric'
                            ? t('metric').toString().toLowerCase()
                            : t('milestone').toString().toLowerCase(),
                      }).toString();
                      return (
                        <Typeahead.Row
                          key={option.optionId}
                          className={cx(
                            styles.metricTitleTypeaheadCreateRow,
                            isCreateRowHighlighted &&
                              styles.metricTitleTypeaheadCreateRowHighlighted
                          )}
                          value={option.optionId}
                          onClick={createFromSearch}
                          withLeftAccessory={<UIIcon src={AddLine} size="small" />}
                          withTitle={
                            <Text size="small" isBold>
                              {createLabel}
                            </Text>
                          }
                          aria-label={createLabel}
                        />
                      );
                    }
                    if (option.kind === 'section') {
                      return (
                        <div
                          key={option.optionId}
                          className={styles.metricTitleTypeaheadSectionRow}
                        >
                          <div className={styles.metricTitleTypeaheadSectionRowContent}>
                            <div className={styles.metricTitleTypeaheadSectionRowTitle}>
                              {renderSectionTitle(option)}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    const title = getPlainTextTitle(option.title).trim() || t('metric').toString();
                    return (
                      <Typeahead.Row
                        key={option.optionId}
                        className={styles.metricTitleTypeaheadRow}
                        value={option.optionId}
                        onClick={() => onSelect(option)}
                        onFocus={clearCreateRowHighlight}
                        onMouseEnter={clearCreateRowHighlight}
                        withTitle={
                          <Text size="small">
                            <Truncate maxWidth={250} tooltipControlProps={{placement: 'top-start'}}>
                              {title}
                            </Truncate>
                          </Text>
                        }
                        aria-label={`${title} ${option.subtitle ?? ''}`.trim()}
                      />
                    );
                  }}
                />
              ) : null}
              {isLoading ? (
                <Typeahead.Row
                  disabled
                  className={styles.metricTitleTypeaheadLoadingRow}
                  withTitle={<Spinner size="small" />}
                  aria-label="Loading metrics"
                />
              ) : null}
            </Typeahead.Container>
          </div>
        );
      }}
    </Typeahead.Wrapper>
  );
};
